import { padTwo } from '@src/utils/date-utils';

export const USE_CASES = [
  { key: 'interview', label: 'Interviews & Assessments', description: 'Score candidate answers to find the strongest fit.' },
  { key: 'employeeExperience', label: 'Employee Experience & Feedback', description: 'Understand how employees feel and what drives their engagement.' },
  { key: 'customerTestimonial', label: 'Customer Testimonial & UGC', description: 'Score how convincing, specific and shareable each customer video is.' },
  { key: 'training', label: 'Training & Enablement', description: 'Check what learners understood and whether they can put it into practice.' },
  { key: 'customerIntelligence', label: 'Customer & Marketing Intelligence', description: 'Learn what customers need, feel and weigh when they decide, in their own words.' },
];

// One line per template, summarising the rubric the evaluation lambda scores it with.
export const TEMPLATE_DESCRIPTIONS = {
  confidenceLevel: 'How confident, composed and assertive the delivery is.',
  languageProficiency: 'Grammar, vocabulary, organisation and fluency of the answer.',
  behavioral: 'Concrete examples of how the candidate acts, decides and works with others.',
  custom: 'Your own criteria, scored against every video.',
  employeeEngagement: 'Enthusiasm, purpose, commitment and the drive to go beyond the role.',
  employeeSentiment: 'How positive or negative the experience is, how strongly, and why.',
  belongingCulture: 'Belonging, psychological safety, voice, fairness and team connection.',
  managerTeamExperience: 'Support, communication, coaching, recognition and trust from the manager.',
  retentionRisk: 'Intent to stay, frustration and workload signals, and what would keep them.',
  customerTestimonial: 'The problem, the solution and the results, told credibly enough to reuse.',
  customerStory: 'A clear before, turning point and after, with results that matter.',
  productReview: 'How the product is shown, what is valued, and whether they would recommend it.',
  socialUgc: 'Hook, natural delivery and how ready the video is for short-form social.',
  customerAdvocacy: 'How strongly and specifically the customer recommends the brand.',
  knowledgeComprehension: 'Whether the learner understands and can explain the key concepts.',
  practicalApplication: 'Applying what was learned to a scenario with sound judgment.',
  processExecution: 'Performing each step correctly, in order and within the standards.',
  communicationDelivery: 'Clarity, structure and confidence when explaining the material.',
  rolePlayPerformance: 'How well the learner handles a realistic conversation or scenario.',
  readinessMastery: 'Whether the learner is ready to perform on their own.',
  needsMotivations: 'The needs, motivations and triggers behind what customers are looking for.',
  painPoints: 'The problems customers face, how much they matter and how well they are solved today.',
  purchaseDrivers: 'What drives the decision to buy, what holds it back and the alternatives considered.',
  brandPerception: 'How customers see the brand or product: benefits, trust, quality and associations.',
  emotionalResponse: 'How strongly customers react, how certain they are and what triggers the reaction.',
};

// Templates whose dimensions are all described with labels instead of scored: they never have a score.
// Shown to the campaign owner before a campaign starts being scored: on turning Insights on, and when creating one
// from a template that comes with Insights.
export const INSIGHTS_NOTICE = 'Video responses to this campaign will be analyzed by VideoPeel AI, which scores each response against the templates you choose.';

export const QUALITATIVE_TEMPLATE_KEYS = ['needsMotivations'];

// Known here too, so a signal score never reads as a quality one while the catalog loads or when it fails.
const SIGNAL_TEMPLATE_KEYS = ['needsMotivations', 'painPoints', 'purchaseDrivers', 'brandPerception', 'emotionalResponse'];

export const FALLBACK_TEMPLATES = [
  { key: 'confidenceLevel', name: 'Confidence Level', useCase: 'interview' },
  { key: 'languageProficiency', name: 'Language Proficiency', useCase: 'interview' },
  { key: 'behavioral', name: 'Behavioral', useCase: 'interview' },
  { key: 'custom', name: 'Custom', useCase: 'interview' },
];

// color paints rings and swatches; textColor and background make a readable badge (the theme's green
// and amber are too light for text on white).
const SCORE_BANDS = [
  { min: 85, label: 'Excellent', range: '85+', color: 'success.dark', textColor: '#1b5e20', background: '#dcefdd' },
  { min: 70, label: 'Strong', range: '70–84', color: 'success.main', textColor: '#2e7d32', background: '#e8f5e9' },
  { min: 50, label: 'Moderate', range: '50–69', color: 'warning.main', textColor: '#8a5a00', background: '#fff4d6' },
  { min: 0, label: 'Weak', range: 'below 50', color: 'error.main', textColor: '#b3353c', background: '#fdecec' },
];

// A signal score says how strongly something shows (a severe pain point, a strong barrier), not that it
// went well, so its bands are neutral.
const SIGNAL_BANDS = [
  { min: 70, label: 'Strong signal', range: '70+', color: 'primary.dark', textColor: '#1d4f91', background: '#e3edf9' },
  { min: 50, label: 'Moderate signal', range: '50–69', color: 'primary.light', textColor: '#3f5f86', background: '#eef3f9' },
  { min: 0, label: 'Weak signal', range: 'below 50', color: 'grey.500', textColor: '#5f6b7a', background: '#f1f3f5' },
];

export const BANDS_BY_SCALE = { quality: SCORE_BANDS, signal: SIGNAL_BANDS };

export const STRONG_SCORE = 70;

export const toDisplayScore = (score) =>
  score === null || score === undefined ? null : Math.round(score * 10);

export const getScoreBand = (displayScore, scale = 'quality') =>
  displayScore === null || displayScore === undefined
    ? null
    : (BANDS_BY_SCALE[scale] || SCORE_BANDS).find((band) => displayScore >= band.min);

// How to read each template's score, from the template catalog; older APIs send no scale.
const knownScale = (key) => (SIGNAL_TEMPLATE_KEYS.includes(key) ? 'signal' : 'quality');

export const scalesByTemplate = (catalog) => ({
  ...Object.fromEntries(SIGNAL_TEMPLATE_KEYS.map((key) => [key, 'signal'])),
  ...Object.fromEntries((catalog || []).map((template) => [template.key, template.scale || knownScale(template.key)])),
});

// The overall score averages the quality templates; only a video evaluated with signal templates alone
// gets an overall signal score (the evaluation lambda's rule).
export const overallScale = (templateKeys, scales) =>
  templateKeys.length > 0 && templateKeys.every((key) => scales[key] === 'signal') ? 'signal' : 'quality';

const evaluatedKeys = (row) => Object.keys(row.scores).filter((key) => row.scores[key] !== undefined);

// How a ranking row's overall reads: decided by the templates it was evaluated with, as in the lambda.
export const rowOverallScale = (row, scales) => overallScale(evaluatedKeys(row), scales);

// Evaluated only with qualitative templates: nothing was scored, but the video has findings.
export const isQualitativeOnlyRow = (row) => {
  const keys = evaluatedKeys(row);
  return keys.length > 0 && keys.every((key) => QUALITATIVE_TEMPLATE_KEYS.includes(key));
};

export const overallLabel = (scale) => (scale === 'signal' ? 'Insight strength' : 'Overall score');

export const scoreOfTemplate = (templateResult) => {
  if (!templateResult) return null;
  const score = templateResult.templateScore !== undefined
    ? templateResult.templateScore
    : templateResult.score;
  return score === undefined ? null : score;
};

const mean = (values) => values.reduce((total, value) => total + value, 0) / values.length;

// A response is every video one person sent to the campaign, usually one per question. Its score for a
// template is the mean of its videos scored with it. null: evaluated, but nothing applied in any video.
// undefined: no video was evaluated with it. This one rule serves the ranking and the Insights tab.
export const averageScore = (scores) => {
  const scored = scores.filter((score) => score !== null && score !== undefined);
  if (scored.length) return mean(scored);
  return scores.some((score) => score === null) ? null : undefined;
};

// The overall of a response with several videos, from its template scores, by the evaluation lambda's rule:
// the quality templates when there are any, the signal ones otherwise; unscored templates stay out.
export const overallOfTemplates = (scores, scales) => {
  const keys = Object.keys(scores).filter((key) => scores[key] !== undefined);
  const scale = overallScale(keys, scales);
  const pool = keys.filter((key) => (scales[key] || 'quality') === scale).map((key) => scores[key]).filter((score) => score !== null);
  return pool.length ? mean(pool) : null;
};

const BEST_CLIPS_PER_RESPONSE = 3;

// What the videos of a response named, per dimension, each idea once however it was worded.
const mergeLabels = (videos) => {
  const byDimension = new Map();
  videos.forEach((video) => video.labels.forEach(({ templateKey, key, name, labels }) => {
    const id = `${templateKey}.${key}`;
    if (!byDimension.has(id)) byDimension.set(id, { templateKey, key, name, labels: [], seen: new Set() });
    const dimension = byDimension.get(id);
    (labels || []).forEach((label) => {
      const normalized = normalizeLabel(label);
      if (!normalized || dimension.seen.has(normalized)) return;
      dimension.seen.add(normalized);
      dimension.labels.push(label);
    });
  }));
  return [...byDimension.values()].map(({ templateKey, key, name, labels }) => ({ templateKey, key, name, labels }));
};

// One ranking row per response. A response with one video, the usual case, is that video as evaluated;
// with several, its scores are their means and its best moments and labels come from all of them.
export const buildRankingRows = (rankings, submissions, templateKeys, scales = {}) => {
  const videoByMediaId = {};
  (submissions || []).forEach((submission) => {
    (submission.videoContent || []).forEach((video, position) => {
      videoByMediaId[video.mediaId] = { submission, video, position };
    });
  });

  const scoreByTemplate = {};
  templateKeys.forEach((key) => {
    scoreByTemplate[key] = {};
    (rankings?.[key] || []).forEach(({ mediaId, score }) => {
      scoreByTemplate[key][mediaId] = score;
    });
  });

  // Evaluations outlive deleted videos, but the submissions list already excludes those, so an
  // evaluated video with no submission is gone (or out of the viewer's reach) and is left out.
  const videosBySubmission = new Map();
  (rankings?.overall || [])
    .filter(({ mediaId }) => videoByMediaId[mediaId])
    .forEach(({ mediaId, overallScore, clips, labels }) => {
      const { submission, video, position } = videoByMediaId[mediaId];
      const id = submission.campaignSubmissionId;
      if (!videosBySubmission.has(id)) videosBySubmission.set(id, { submission, videos: [] });
      videosBySubmission.get(id).videos.push({
        mediaId,
        position,
        question: video.question,
        thumbnailUrl: video.thumbnailUrl,
        overall: overallScore ?? null,
        // The video's best moments, chosen by the API; older APIs send none.
        clips: (clips || []).map((clip) => ({ ...clip, mediaId })),
        // What its qualitative dimensions named, for the campaign's top results; older APIs send none.
        labels: labels || [],
        // undefined: never evaluated with the template. null: evaluated, but nothing in it applied.
        scores: Object.fromEntries(templateKeys.map((key) => [
          key,
          mediaId in scoreByTemplate[key] ? scoreByTemplate[key][mediaId] ?? null : undefined
        ])),
      });
    });

  return [...videosBySubmission.values()].map(({ submission, videos: evaluated }) => {
    // In the order the response shows them, so "first video" means the same everywhere.
    const videos = [...evaluated].sort((a, b) => a.position - b.position);
    const [first] = videos;
    const single = videos.length === 1;
    const scores = single
      ? first.scores
      : Object.fromEntries(templateKeys.map((key) => [key, averageScore(videos.map((video) => video.scores[key]))]));
    return {
      // The video a row opens on, and its identity in lists.
      mediaId: first.mediaId,
      submissionId: submission.campaignSubmissionId,
      submitterName: submission.submitterName,
      videoCount: videos.length,
      question: single ? first.question : null,
      thumbnailUrl: first.thumbnailUrl,
      // One video keeps the overall the lambda stored for it.
      overall: single ? first.overall : overallOfTemplates(scores, scales),
      clips: videos.flatMap((video) => video.clips).sort((a, b) => b.score - a.score).slice(0, BEST_CLIPS_PER_RESPONSE),
      labels: single ? first.labels : mergeLabels(videos),
      scores,
    };
  });
};

// Headline numbers for the ranking, on the 0–100 scale. A response whose templates were all N/A was
// evaluated but has no overall score, so it stays out of the average. scoredRows narrows the average and
// the strong count to rows whose overall reads on one scale, so quality and signal scores never mix.
export const summarizeRanking = (rows, submissions, scoredRows = rows) => {
  const overalls = scoredRows.map((row) => toDisplayScore(row.overall)).filter((score) => score !== null);
  const withVideos = (submissions || []).filter((submission) => (submission.videoContent || []).length);
  return {
    evaluatedCount: rows.length,
    responseCount: withVideos.length,
    evaluatedVideoCount: rows.reduce((total, row) => total + (row.videoCount || 1), 0),
    videoCount: withVideos.reduce((total, submission) => total + submission.videoContent.length, 0),
    average: overalls.length ? Math.round(mean(overalls)) : null,
    strongCount: overalls.filter((score) => score >= STRONG_SCORE).length,
  };
};

// Labels are the model's own short phrases, so the same idea comes back worded slightly differently:
// "Personalization & engraving", "personalization / engraving". Case, punctuation, "and" and simple plurals
// are ignored when counting.
export const normalizeLabel = (label) =>
  label
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter((word) => word && word !== 'and')
    .map((word) => (word.length > 3 && word.endsWith('s') && !word.endsWith('ss') ? word.slice(0, -1) : word))
    .join(' ');

export const TOP_RESULTS_PER_DIMENSION = 3;

// What the videos' qualitative dimensions named most, by template (in templateOrder) and dimension: each
// result counts the videos that named it, worded as it was first seen. Ties keep the order of first mention.
// A result carries what identifies it (template, dimension and normalized label) so it can filter the ranking.
export const topResults = (rows, templateOrder) => {
  const dimensions = new Map();
  rows.forEach((row) => {
    (row.labels || []).forEach(({ templateKey, key, name, labels }) => {
      const id = `${templateKey}.${key}`;
      if (!dimensions.has(id)) dimensions.set(id, { templateKey, key, name, results: new Map() });
      const { results } = dimensions.get(id);
      new Set((labels || []).map(normalizeLabel).filter(Boolean)).forEach((normalized) => {
        if (!results.has(normalized)) results.set(normalized, { label: labels.find((label) => normalizeLabel(label) === normalized), normalized, count: 0 });
        results.get(normalized).count += 1;
      });
    });
  });

  const byTemplate = new Map();
  dimensions.forEach(({ templateKey, key, name, results }) => {
    if (!byTemplate.has(templateKey)) byTemplate.set(templateKey, []);
    byTemplate.get(templateKey).push({
      key,
      name,
      results: [...results.values()]
        .sort((a, b) => b.count - a.count)
        .slice(0, TOP_RESULTS_PER_DIMENSION)
        .map(({ label, normalized, count }) => ({ templateKey, key, dimension: name, label, normalized, count })),
    });
  });
  const position = (key) => (templateOrder.includes(key) ? templateOrder.indexOf(key) : templateOrder.length);
  return [...byTemplate.keys()]
    .sort((a, b) => position(a) - position(b))
    .map((templateKey) => ({ templateKey, dimensions: byTemplate.get(templateKey) }));
};

// The results named by the most videos across every dimension, for the collapsed top results.
export const topHighlights = (groups, count = 3) =>
  groups
    .flatMap((group) => group.dimensions.flatMap((dimension) => dimension.results))
    .sort((a, b) => b.count - a.count)
    .slice(0, count);

export const isSameResult = (a, b) =>
  !!a && !!b && a.templateKey === b.templateKey && a.key === b.key && a.normalized === b.normalized;

// Whether a ranking row's video named this top result.
export const rowNamesResult = (row, result) =>
  (row.labels || []).some((entry) => entry.templateKey === result.templateKey && entry.key === result.key
    && (entry.labels || []).some((label) => normalizeLabel(label) === result.normalized));

// Ranking period filter: calendar days in the viewer's time zone, as YYYY-MM-DD (a date input's value).
export const toLocalDateKey = (date) => {
  const day = new Date(date);
  return [day.getFullYear(), padTwo(day.getMonth() + 1), padTwo(day.getDate())].join('-');
};

// Both ends are inclusive; an empty end leaves that side open.
export const isInDateRange = (date, { from, to }) => {
  if (!from && !to) return true;
  if (!date) return false;
  const day = toLocalDateKey(date);
  return (!from || day >= from) && (!to || day <= to);
};

export const DATE_RANGE_PRESETS = [
  { key: 'all', label: 'All time', days: null },
  { key: 'last7', label: 'Last 7 days', days: 7 },
  { key: 'last30', label: 'Last 30 days', days: 30 },
  { key: 'last90', label: 'Last 90 days', days: 90 },
];

export const presetDateRange = (days, today = new Date()) => {
  if (!days) return { from: '', to: '' };
  const start = new Date(today);
  start.setDate(start.getDate() - (days - 1));
  return { from: toLocalDateKey(start), to: toLocalDateKey(today) };
};

// The campaign ranking lives in the Insights hub.
export const insightsReportPath = (campaignLink) => `/insights/reports/${campaignLink}`;

// Clip times as m:ss, the way the video player shows them.
export const formatClipTime = (seconds) => {
  const whole = Math.floor(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
};



// WEBPACK FOOTER //
// ./src/constants/insights.js